/**
 * WriteApprovalModal - Confirmation dialog for write tool execution
 * Shows when Claude wants to execute a write operation (create, update, delete)
 * and requires explicit user approval before proceeding.
 */

import { useState } from 'react'
import { ShieldAlert, Check, X, Loader2 } from 'lucide-react'
import { approveToolExecution } from '../../lib/api'

// Human-readable tool name mapping
const TOOL_LABELS = {
  // Sales Orders
  create_sales_order: 'Create Sales Order',
  add_order_line: 'Add Order Line',
  update_order: 'Update Sales Order',
  update_order_line: 'Update Order Line',
  convert_quote_to_order: 'Convert Quote to Order',
  cancel_order_line: 'Cancel Order Line',
  // Quotes
  create_quote: 'Create Quote',
  update_quote: 'Update Quote',
  update_quote_status: 'Update Quote Status',
  mark_quote_won: 'Mark Quote Won',
  mark_quote_lost: 'Mark Quote Lost',
  duplicate_quote: 'Duplicate Quote',
  // Jobs
  create_job: 'Create Job',
  close_job: 'Close Job',
  complete_job: 'Complete Job',
  release_job: 'Release Job',
  update_job: 'Update Job',
  report_scrap: 'Report Scrap',
  // POs
  create_po: 'Create Purchase Order',
  add_po_line: 'Add PO Line',
  update_po: 'Update PO',
  approve_po: 'Approve PO',
  receive_po: 'Receive PO',
  // Invoicing
  create_cash_receipt: 'Create Cash Receipt',
  create_dep_mis_invoice: 'Create Deposit Invoice',
  create_shipment_invoice: 'Create Shipping Invoice',
  void_invoice: 'Void Invoice',
  create_credit_memo: 'Create Credit Memo',
  create_debit_memo: 'Create Debit Memo',
  // Shipping
  create_shipment: 'Create Shipment',
  ship_confirm: 'Ship Confirm',
  void_shipment: 'Void Shipment',
  // Customer
  create_customer: 'Create Customer',
  update_customer: 'Update Customer',
  set_credit_hold: 'Set Credit Hold',
  // CRM
  create_crm_call: 'Log CRM Activity',
  update_crm_call: 'Update CRM Activity',
  // Email
  send_email: 'Send Email',
  email_document: 'Email Document',
}

function getToolLabel(toolName) {
  return TOOL_LABELS[toolName] || toolName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// Extract key details from tool input for display
function getInputSummary(toolName, input) {
  if (!input || typeof input !== 'object') return null

  const details = []

  // Customer context
  if (input.cust_num) details.push(`Customer #${input.cust_num}`)
  if (input.customer_name) details.push(input.customer_name)

  // Order/Quote/Job context
  if (input.order_num) details.push(`Order ${input.order_num}`)
  if (input.quote_num) details.push(`Quote ${input.quote_num}`)
  if (input.job_num) details.push(`Job ${input.job_num}`)
  if (input.po_num) details.push(`PO ${input.po_num}`)
  if (input.invoice_num) details.push(`Invoice ${input.invoice_num}`)
  if (input.pack_num) details.push(`Pack ${input.pack_num}`)

  // Amount context
  if (input.amount) details.push(`$${Number(input.amount).toLocaleString()}`)
  if (input.receipt_amt) details.push(`$${Number(input.receipt_amt).toLocaleString()}`)

  // Part context
  if (input.part_num) details.push(`Part ${input.part_num}`)
  if (input.quantity) details.push(`Qty: ${input.quantity}`)

  // Description/comment
  if (input.call_desc) details.push(input.call_desc)
  if (input.task_description) details.push(input.task_description)

  return details.length > 0 ? details : null
}

export default function WriteApprovalModal({ approvalId, toolName, toolInput, onClose }) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const handleResponse = async (approved) => {
    setSubmitting(true)
    setError(null)
    try {
      await approveToolExecution(approvalId, approved)
      onClose(approved)
    } catch (err) {
      console.error('Approval error:', err)
      setError(err.message || 'Failed to send response')
      setSubmitting(false)
    }
  }

  const label = getToolLabel(toolName)
  const summary = getInputSummary(toolName, toolInput)

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => !submitting && handleResponse(false)}
      />

      {/* Modal */}
      <div className="relative bg-[var(--bg-primary)] border border-[var(--border-primary)] rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--border-primary)] bg-amber-500/10">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-500/20">
            <ShieldAlert className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-[var(--text-primary)]">
              Write Operation
            </h3>
            <p className="text-xs text-[var(--text-secondary)]">
              Claude wants to execute a write action
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--bg-secondary)]">
            <span className="text-sm font-medium text-[var(--text-primary)]">
              {label}
            </span>
          </div>

          {summary && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                Details
              </p>
              <div className="flex flex-wrap gap-1.5">
                {summary.map((detail, i) => (
                  <span
                    key={i}
                    className="inline-flex px-2 py-0.5 text-xs rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)]"
                  >
                    {detail}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Raw params (collapsed) */}
          {toolInput && Object.keys(toolInput).length > 0 && (
            <details className="group">
              <summary className="text-xs text-[var(--text-tertiary)] cursor-pointer hover:text-[var(--text-secondary)]">
                View full parameters
              </summary>
              <pre className="mt-1 p-2 text-xs rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)] overflow-x-auto max-h-32 overflow-y-auto">
                {JSON.stringify(toolInput, null, 2)}
              </pre>
            </details>
          )}

          {error && (
            <p className="text-xs text-red-500">{error}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-5 py-4 border-t border-[var(--border-primary)]">
          <button
            onClick={() => handleResponse(false)}
            disabled={submitting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[var(--border-primary)] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" />
            Deny
          </button>
          <button
            onClick={() => handleResponse(true)}
            disabled={submitting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Approve
          </button>
        </div>
      </div>
    </div>
  )
}
